// ─────────────────────────────────────────────────────────────────
// Glass-i — UOM Master API client (Stage 6B.8A-1).
//
// Mirrors supplierGroupApi shape: list / lookup / get / create /
// update / status / remove. The grid sends camelCase field names;
// we translate to snake_case keys the backend whitelist understands.
// ─────────────────────────────────────────────────────────────────

import client from '../../../../api/client';

const BASE = '/api/erp/masters/uoms';

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

// camelCase grid field → snake_case backend whitelist key.
const GRID_FIELD_TO_SQL = {
  uomCode:          'uom_code',
  uomName:          'uom_name',
  uomSymbol:        'uom_symbol',
  uqcCode:          'uqc_code',
  uqcName:          'uqc_name',
  measurementType:  'measurement_type',
  decimalPrecision: 'decimal_precision',
  isGstStandard:    'is_gst_standard',
  isSystem:         'is_system',
  isActive:         'is_active',
  sortOrder:        'sort_order',
  createdAt:        'created_at',
  updatedAt:        'updated_at'
};

function mapField(camel) {
  if (!camel || typeof camel !== 'string') return camel;
  return GRID_FIELD_TO_SQL[camel] || camel;
}

function translateAdvancedRules(rules) {
  return rules.map((r) => {
    if (!r || typeof r !== 'object') return r;
    if (Array.isArray(r.rules)) return { ...r, rules: translateAdvancedRules(r.rules) };
    if (typeof r.field === 'string' && r.field) return { ...r, field: mapField(r.field) };
    return r;
  });
}

function translateQuery(q) {
  if (!q || typeof q !== 'object') return q;
  const out = { ...q };
  if (Array.isArray(out.filters)) {
    out.filters = out.filters
      .filter((f) => f && f.field)
      .map((f) => ({ ...f, field: mapField(f.field) }));
  }
  if (Array.isArray(out.sort)) {
    out.sort = out.sort
      .filter((s) => s && s.field)
      .map((s) => ({ ...s, field: mapField(s.field) }));
  }
  if (typeof out.groupBy === 'string' && out.groupBy) {
    out.groupBy = mapField(out.groupBy);
  }
  if (out.advancedFilters && Array.isArray(out.advancedFilters.rules)) {
    out.advancedFilters = {
      ...out.advancedFilters,
      rules: translateAdvancedRules(out.advancedFilters.rules)
    };
  }
  return out;
}

export const uomApi = {
  list: (query) =>
    client.post(`${BASE}/list`, translateQuery(query) || {}).then(unwrap),
  lookup: ({ q = '', measurementType = null, limit = 200 } = {}) =>
    client.get(`${BASE}/lookup`, {
      params: { q, measurementType: measurementType || undefined, limit }
    }).then(unwrap).then((d) => (d && Array.isArray(d.rows) ? d.rows : [])),
  get: (id) =>
    client.get(`${BASE}/${encodeURIComponent(id)}`).then(unwrap),
  create: (payload) =>
    client.post(BASE, payload).then(unwrap),
  update: (id, payload) =>
    client.put(`${BASE}/${encodeURIComponent(id)}`, payload).then(unwrap),
  updateStatus: (id, body) =>
    client.patch(`${BASE}/${encodeURIComponent(id)}/status`, body).then(unwrap),
  remove: (id) =>
    client.delete(`${BASE}/${encodeURIComponent(id)}`).then(unwrap)
};

export default uomApi;
